/* Notes:  this component  works well for bulk edit so far,
     might need some works here then re-use it  when we migrate event detail page
*/

import {
    ChangeDetectionStrategy,
    Component,
    computed,
    linkedSignal,
    OnInit,
    ViewEncapsulation,
    input,
    viewChild,
    numberAttribute,
} from "@angular/core";
import { Nullable } from "../../../pojo/Util";
import { GenericDropdownUtil } from "../../s25-dropdown/generic/generic.dropdown.util";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ModalImageI } from "../../modal/modals/modal.image.upload.component";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Bind } from "../../../decorators/bind.decorator";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { S25File, FileUploadComponent } from "../../s25-file-upload/s25.file.upload.component";
import { AttachmentService, FileItem } from "../../../services/attachment.service";
import { Item } from "../../../pojo/Item";
import { MasterDefinitionUtil } from "../../../util/master.definition.util";
import { S25Bool } from "../../../util/core/s25.bool";
import { S25Number } from "../../../util/core/s25.number";
import ItemValue = CustomAttributes.ItemValue;
import { Proto } from "../../../pojo/Proto";
import NumericalBoolean = Proto.NumericalBoolean;
import { FileStoreParams, FileStoreUtil } from "../../../util/filestore.util";
import { maybeDeleteOldFileAfterOrgCaReplace } from "../../../util/org.filestore.ref.util";
import { ModalService } from "../../modal/modal.service";
import {
    ModalDuplicateFileUploadComponent,
    suggestUniqueFilename,
} from "../../modal/modals/modal.duplicate.file.upload.component";
import { FILESTORE_UPLOAD_COMMIT_NONE, FilestoreUploadCommitPlan } from "../../../util/filestore-upload-commit.util";
import { isDefined } from "../../../util/core";
import { s25Resource } from "../../../util/s25.resource";
import { PartialStateUpdater, patchState, signalState } from "@ngrx/signals";

type Upload = { file: S25File | undefined; commitPlan: FilestoreUploadCommitPlan };

namespace Upload {
    export const NothingStaged: Upload = { file: undefined, commitPlan: FILESTORE_UPLOAD_COMMIT_NONE };

    export function stage(
        file: S25File,
        commitPlan: FilestoreUploadCommitPlan = FILESTORE_UPLOAD_COMMIT_NONE,
    ): PartialStateUpdater<Upload> {
        return () => ({ file, commitPlan });
    }

    export function clearStaged(): PartialStateUpdater<Upload> {
        return () => NothingStaged;
    }
}

@TypeManagerDecorator("s25-ng-editable-custom-attribute")
@Component({
    selector: "s25-ng-editable-custom-attribute",
    template: `
        <ng-container>
            <div class="attributeRow">
                @if (attributeName()) {
                    <label [id]="fieldId() + '-label'" [for]="fieldId()" class="ngBlock attribute-label">
                        <s25-ng-link-parser [text]="attributeName()!"></s25-ng-link-parser><span class="colon">:</span>
                    </label>
                }

                @switch (attributeType()) {
                    @case (AttributeType.Boolean) {
                        <s25-toggle-button
                            [value]="booleanValue()"
                            (valueChange)="onValueChangeCommit($event)"
                            [toggleLabel]="{ by: fieldId() + '-label' }"
                            name="attribute value"
                            [disabled]="!editable()"
                        ></s25-toggle-button>
                    }

                    @case (AttributeType.Text) {
                        @if (!discreteOptions()) {
                            <s25-ng-editable-text
                                (valChange)="onValueChange($event)"
                                [val]="value()"
                                [fieldId]="fieldId()"
                                [ariaLabel]="{ by: fieldId() + '-label' }"
                                [hasCommit]="hasCommit"
                                [onCommit]="onCommit"
                                [readOnly]="!editable()"
                                name="attribute value"
                            ></s25-ng-editable-text>
                        } @else if (multiVal() === 0) {
                            @if (editable()) {
                                <select
                                    [ngModel]="value()"
                                    (ngModelChange)="onValueChangeCommit($event)"
                                    [id]="fieldId()"
                                    class="cn-form__control"
                                    name="attribute value"
                                >
                                    @for (opt of discreteOptions(); track opt.itemId) {
                                        <option [value]="opt.itemId">
                                            {{ opt.itemName }}
                                        </option>
                                    }
                                </select>
                            } @else {
                                <p class="text-value">{{ value() }}</p>
                            }
                        } @else if (multiVal() === 1) {
                            <div>
                                @if (editable()) {
                                    <s25-ng-generic-multiselect-dropdown
                                        [items]="discreteOptions()!"
                                        [onDone]="onDone"
                                        [value]="multiVals()"
                                        (valueChange)="value.set($event ?? null)"
                                        [fieldId]="fieldId()"
                                        [ariaLabel]="{ by: fieldId() + '-label' }"
                                    />
                                }
                                <p class="text-value">{{ textMultiVals() }}</p>
                            </div>
                        }
                    }

                    @case (AttributeType.Integer) {
                        <s25-ng-editable-number
                            (valChange)="onValueChange($event)"
                            [val]="numberValue()"
                            [fieldId]="fieldId()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            [hasCommit]="hasCommit"
                            [onCommit]="onCommit"
                            [readOnly]="!editable()"
                            [allowEmpty]="false"
                            name="attribute value"
                        ></s25-ng-editable-number>
                    }

                    @case (AttributeType.Float) {
                        <s25-ng-editable-number
                            (valChange)="onValueChange($event)"
                            [val]="numberValue()"
                            [fieldId]="fieldId()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            [hasCommit]="hasCommit"
                            [onCommit]="onCommit"
                            [readOnly]="!editable()"
                            [type]="'float'"
                            [min]="-9999999"
                            [allowEmpty]="false"
                            name="attribute value"
                        ></s25-ng-editable-number>
                    }

                    @case (AttributeType.LongText) {
                        <s25-ng-editable-textarea
                            (valChange)="onValueChange($event)"
                            [val]="value()"
                            [fieldId]="fieldId()"
                            [rows]="10"
                            [cols]="50"
                            [hasCommit]="hasCommit"
                            [onCommit]="onCommit"
                            [fadePreview]="fadePreview"
                            [readOnly]="!editable()"
                            [renderHtml]="true"
                            name="attribute value"
                        ></s25-ng-editable-textarea>
                    }

                    @case (AttributeType.URL) {
                        <div>
                            @if (editable()) {
                                @if (hasUploadFile() && !isUrlOnly()) {
                                    <s25-toggle-button
                                        [(value)]="isUploaded"
                                        [trueLabel]="'Upload'"
                                        [falseLabel]="'Link'"
                                        class="ngBlock c-margin-bottom--single"
                                    ></s25-toggle-button>
                                }
                                @if (!isUploaded()) {
                                    <s25-ng-editable-text
                                        (valChange)="onValueChange($event)"
                                        [val]="value()"
                                        [fieldId]="fieldId()"
                                        [ariaLabel]="{ by: fieldId() + '-label' }"
                                        [hasCommit]="hasCommit"
                                        [onCommit]="onCommit"
                                        [readOnly]="false"
                                        name="attribute value"
                                    ></s25-ng-editable-text>
                                } @else if (hasUploadFile()) {
                                    <s25-ng-file-upload
                                        [fieldId]="fieldId()"
                                        [multiple]="false"
                                        [showFiles]="false"
                                        [onAdd]="handleFileUpload"
                                    />
                                }
                            } @else if (!isUploaded()) {
                                @if (urlValue()) {
                                    <a [href]="urlValue()" target="_self">Click Here</a>
                                }
                            }

                            @if (hasStagedFile()) {
                                {{ stagedUpload.file()?.name }}
                            } @else if (isUploaded()) {
                                <s25-ng-display-file
                                    class="c-margin-bottom--half"
                                    [fileName]="fileName()"
                                    [objectId]="fileObjectId()"
                                    [objectType]="fileStoreParams()?.objectType"
                                    [fileKey]="fileStoreParams()?.fileKey"
                                />
                            }
                            <!--   when migration event form, might need to ajust this, I think only event form use this logic -->
                            @if (
                                editEventImage() &&
                                (attributeId() === -20 || attributeId() === -21) &&
                                itemTypeId() === 1
                            ) {
                                <s25-ng-editable-image
                                    [showImage]="true"
                                    [val]="value()"
                                    [fieldId]="fieldId()"
                                    [onCommit]="imageAction"
                                    (valChange)="onValueChange($event)"
                                    [readOnly]="!editable()"
                                    [autoSaveUpload]="true"
                                ></s25-ng-editable-image>
                            }
                        </div>
                    }

                    @case (AttributeType.Date) {
                        <s25-ng-editable-date
                            (valChange)="onValueChangeCommit($event)"
                            [val]="value()"
                            [inputId]="fieldId()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            [readOnly]="!editable()"
                            [allowEmpty]="false"
                            name="attribute value"
                        ></s25-ng-editable-date>
                    }

                    @case (AttributeType.DateTime) {
                        <s25-ng-editable-date-time
                            (valChange)="onValueChangeCommit($event)"
                            [val]="value()"
                            [dateInputId]="fieldId()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            [outputString]="true"
                            [readOnly]="!editable()"
                            name="attribute value"
                        ></s25-ng-editable-date-time>
                    }

                    @case (AttributeType.Time) {
                        <s25-timepicker
                            (modelValueChange)="onValueChangeCommit($event)"
                            [defaultNow]="true"
                            [modelValue]="value()"
                            [inputId]="fieldId()"
                            name="attribute value"
                        ></s25-timepicker>
                    }

                    @case (AttributeType.Location) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'locations'"
                            [value]="itemValue()"
                            [fieldId]="fieldId()"
                            [blockDetailsNav]="blockDetailsNav()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            (valueChange)="onValueChangeCommit($any($event))"
                            name="attribute value"
                        />
                    }

                    @case (AttributeType.Resource) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'resources'"
                            [value]="itemValue()"
                            [fieldId]="fieldId()"
                            [blockDetailsNav]="blockDetailsNav()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            (valueChange)="onValueChangeCommit($any($event))"
                            name="attribute value"
                        />
                    }

                    @case (AttributeType.Organization) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'organizations'"
                            [value]="itemValue()"
                            [fieldId]="fieldId()"
                            [blockDetailsNav]="blockDetailsNav()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            (valueChange)="onValueChangeCommit($any($event))"
                            name="attribute value"
                        />
                    }

                    @case (AttributeType.Contact) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'contacts'"
                            [value]="itemValue()"
                            [fieldId]="fieldId()"
                            [blockDetailsNav]="blockDetailsNav()"
                            [ariaLabel]="{ by: fieldId() + '-label' }"
                            (valueChange)="onValueChangeCommit($any($event))"
                            name="attribute value"
                        />
                    }

                    @case (AttributeType.Image) {
                        <s25-ng-editable-image
                            [showImage]="true"
                            [val]="value()"
                            [onCommit]="imageAction"
                            (valChange)="onValueChangeCommit($event)"
                            [readOnly]="!editable()"
                            [autoSaveUpload]="true"
                            [hasCommit]="true"
                            [fieldId]="fieldId()"
                        ></s25-ng-editable-image>
                    }
                }
            </div>
        </ng-container>
    `,
    styles: `
        .imagePreview {
            max-width: 20em;
        }

        .text-value {
            overflow-wrap: break-word;
        }

        .attributeRow {
            overflow-wrap: break-word;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditableCustomAttributeComponent
    extends S25EditableAbstract<CustomAttributes.ConditionValue>
    implements OnInit
{
    readonly readOnly = input<boolean>(true);
    readonly attributeType = input.required<CustomAttributes.CustAttrCode>();
    readonly attributeId = input.required({ transform: numberAttribute });
    readonly attributeName = input<string>();
    readonly multiVal = input<NumericalBoolean>(0);
    readonly hasUploadFile = input<boolean>(false);
    readonly deferFilestoreUntilCommit = input<boolean>(false);
    readonly type = input<string>("");
    readonly itemTypeId = input<Item.Ids>();
    readonly objectId = input<number>();
    readonly editEventImage = input<boolean>(false);
    readonly blockDetailsNav = input<boolean>(false);
    readonly resolveCustomAttributeLabel = input<(custAttrId: number) => string | undefined>();
    readonly fileUploadComponent = viewChild(FileUploadComponent);

    readonly allDiscreteOptions = s25Resource(() => CustomAttributeService.getAllDiscreteOptions());

    getType = (): string => this.type() ?? "";

    readonly editable = computed(() => !this.readOnly() && !CustomAttributeService.isReadOnly(this.attributeId()));
    readonly isUrlUploadFile = computed(
        () => this.attributeType() === this.AttributeType.URL && FileStoreUtil.isFileStoreUrl(this.value()),
    );
    readonly fileStoreParams = computed((): FileStoreParams | null => {
        if (this.attributeType() !== this.AttributeType.URL) return null;
        const raw = this.value();
        if (typeof raw !== "string") return null;
        return FileStoreUtil.parseFileStoreUrl(raw);
    });
    readonly fileObjectId = computed(() => this.fileStoreParams()?.objectId ?? this.objectId());
    readonly fileName = computed((): string | undefined => {
        const fileName = this.stagedUpload.file()?.name;
        if (fileName) return String(fileName);
        return this.fileStoreParams()?.fileName;
    });
    readonly urlValue = computed(() => {
        const value = this.value();
        if (!value || typeof value !== "string") return "";
        return value.startsWith("http://") || value.startsWith("https://") ? value : "http://" + value;
    });
    readonly booleanValue = computed(() => {
        const value = this.value();
        return isDefined(value) ? S25Bool.toBool(value) : undefined;
    });
    readonly numberValue = computed(() => {
        const value = S25Number.parseFloat(this.value());
        return isNaN(value) ? undefined : value;
    });
    readonly itemValue = computed((): DropDownItem | undefined => {
        const value = this.value();
        if (!value || typeof value !== "object" || Array.isArray(value) || value instanceof Date) return undefined;
        return value;
    });
    readonly multiVals = computed(() => {
        const value = this.value();
        return Array.isArray(value) ? (value as ItemValue[]) : [];
    });
    readonly textMultiVals = computed(() =>
        this.multiVals()
            .map((item) => item.itemName)
            .filter(isDefined)
            .join("; "),
    );
    readonly isUploaded = linkedSignal(() => this.isUrlUploadFile());
    private readonly validDiscreteOptions = computed(
        () => this.allDiscreteOptions.value()?.find((option) => option.itemId === this.attributeId())?.opt,
    );
    // ANG-6524 If selected values are no longer options, add them to the list so that they still show.
    readonly missingOptions = linkedSignal({
        source: () => ({
            attributeId: this.attributeId(),
            validOptions: this.validDiscreteOptions(),
            multiVal: this.multiVal(),
            value: this.value(),
        }),
        computation: ({ attributeId, validOptions, multiVal, value }, previous): ItemValue[] => {
            const accumulated = previous && previous.source.attributeId === attributeId ? previous.value : [];
            if (!validOptions) return accumulated;
            const options = new Set(validOptions);
            const seen = new Set(accumulated.map((item) => String(item.itemId)));
            const isMissing = (itemId: string) => !options.has(itemId) && !seen.has(itemId);
            const added = multiVal
                ? ((value ?? []) as ItemValue[]).filter((item) => isMissing(String(item.itemId)))
                : isDefined(value) && isMissing(String(value))
                  ? [GenericDropdownUtil.extractItem(String(value))]
                  : [];
            return added.length ? [...accumulated, ...added] : accumulated;
        },
    }).asReadonly();
    readonly discreteOptions = computed(() => {
        const options = this.discreteOptionsFor(this.attributeId());
        return options && [...this.missingOptions(), ...options];
    });

    readonly AttributeType = CustomAttributes.type;
    readonly stagedUpload = signalState<Upload>(Upload.NothingStaged);
    readonly hasStagedFile = computed(() => !!this.stagedUpload.file());

    async onValueChangeCommit(e: any) {
        await this.onValueChange(e);
        this.onCommit?.(e);
    }

    async onValueChange(e: string) {
        if (e === this.value()?.toString()) return;
        this.valChange.emit(e);
        const fileParams = this.fileStoreParams();
        const fileObjectId = this.fileObjectId();
        const itemTypeId = this.itemTypeId();
        if (
            this.attributeType() === this.AttributeType.URL &&
            fileParams?.fileName &&
            isDefined(itemTypeId) &&
            isDefined(fileObjectId)
        ) {
            if (itemTypeId === Item.Ids.Organization) {
                await maybeDeleteOldFileAfterOrgCaReplace(fileObjectId, this.value()?.toString(), fileParams.fileKey);
            } else {
                await AttachmentService.delFile(itemTypeId, fileObjectId, fileParams.fileName, fileParams.fileKey);
            }
            this.value.set(e);
        }
    }

    imageAction = (data: ModalImageI) => {
        this.valChange.emit(data);
        this.onCommit?.(data);
    };

    discreteOptionsFor(attributeId: number): undefined | DropDownItem<string | number>[] {
        return this.allDiscreteOptions
            .value()
            ?.find((discreteOption) => discreteOption.itemId === attributeId)
            ?.opt.map((opt) => GenericDropdownUtil.extractItem(opt));
    }

    @Bind
    onDone({ items }: { items: Nullable<DropDownItem[]> }) {
        this.valChange.emit(items);
        this.onCommit?.(items);
    }

    handleFileUpload = async (file: S25File) => {
        const itemTypeId = this.itemTypeId();
        const objectId = this.fileObjectId();
        if (itemTypeId == null || objectId == null) return;
        const fileKey = this.fileStoreParams()?.fileKey;
        const getFilesList = await AttachmentService.getFileList(itemTypeId, objectId, fileKey).then(function (data) {
            return data || [];
        });
        const sanitizedUploadName = AttachmentService.sanitizeFilenameSpaces(file.name) ?? file.name;
        const conflictingRow = getFilesList.find((listRow) => {
            const listName = AttachmentService.sanitizeFilenameSpaces(listRow.itemName) ?? listRow.itemName;
            return listName === sanitizedUploadName;
        });
        const showDuplicateModal =
            !!conflictingRow && (!this.isSameBindingFilename(file.name) || this.deferFilestoreUntilCommit());
        if (showDuplicateModal) {
            const existingNames = new Set(getFilesList.map((listRow) => listRow.itemName));
            void ModalService.byClass(ModalDuplicateFileUploadComponent, {
                duplicateFileName: file.name ?? "",
                conflictingCustomAttributeName: this.conflictingCustomAttributeDisplayName(conflictingRow),
                incomingFile: file,
                conflictingFile: conflictingRow,
                objectTypeId: itemTypeId,
                objectId: objectId,
                fileKey,
                suggestedFileName: suggestUniqueFilename(file.name ?? "", existingNames),
                existingFileNames: existingNames,
                onReplace: async () => {
                    if (!this.deferFilestoreUntilCommit()) {
                        await AttachmentService.delFile(
                            this.itemTypeId()!,
                            this.fileObjectId()!,
                            conflictingRow.itemName,
                            fileKey,
                        );
                    }
                    const replacePlan: FilestoreUploadCommitPlan = {
                        kind: "replace",
                        deleteAttachmentName: conflictingRow.itemName,
                    };
                    await this.completeLocalUpload(file, replacePlan);
                },
                onUploadWithNewName: async (renamedFile) => {
                    await this.completeLocalUpload(renamedFile, { kind: "rename" });
                },
            });
            this.resetFileUploadInput();
            return;
        }
        await this.completeLocalUpload(file, { kind: "unique" });
    };

    private isSameBindingFilename(uploadName: string | undefined): boolean {
        const fileName = this.fileName();
        if (!uploadName || !fileName) return false;
        const sanitizedUpload = AttachmentService.sanitizeFilenameSpaces(uploadName) ?? uploadName;
        const sanitizedBound = AttachmentService.sanitizeFilenameSpaces(fileName) ?? fileName;
        return sanitizedUpload === sanitizedBound;
    }

    /**
     * When the list row has a CA id and the parent supplied a label lookup, return that name for the modal.
     * No CA on the file, or no resolved label: omit the CA line (undefined).
     */
    private conflictingCustomAttributeDisplayName(conflicting: FileItem): string | undefined {
        if (conflicting.custAtrbId == null) {
            return undefined;
        }
        const label = this.resolveCustomAttributeLabel()?.(conflicting.custAtrbId)?.trim();
        return label || undefined;
    }

    private async completeLocalUpload(chosenFile: S25File, commitPlan: FilestoreUploadCommitPlan) {
        const itemTypeId = this.itemTypeId();
        const objectId = this.fileObjectId();
        if (itemTypeId == null || objectId == null) return;
        const deferFilestoreUntilCommit = this.deferFilestoreUntilCommit();
        const fileParams = this.fileStoreParams();
        if (!deferFilestoreUntilCommit && fileParams?.fileName) {
            if (itemTypeId === Item.Ids.Organization) {
                const prev = typeof this.value() === "string" ? (this.value() as string) : undefined;
                await maybeDeleteOldFileAfterOrgCaReplace(objectId, prev, fileParams.fileKey);
            } else {
                await AttachmentService.delFile(itemTypeId, objectId, fileParams.fileName, fileParams.fileKey);
            }
        }
        patchState(this.stagedUpload, Upload.stage(chosenFile, deferFilestoreUntilCommit ? commitPlan : undefined));
        this.valChange.emit(chosenFile);
        this.onCommit?.(chosenFile);
        this.resetFileUploadInput();
    }

    /** Clears native file input so the same path can be chosen again on the next click. */
    private resetFileUploadInput(): void {
        this.fileUploadComponent()?.reset();
    }

    @Bind
    onRefresh(newVal: CustomAttributes.ConditionValue | null) {
        patchState(this.stagedUpload, Upload.clearStaged());
        this.value.set(newVal);
        this.resetFileUploadInput();
    }

    isUrlOnly(): boolean {
        let type: CustomAttributes.UrlAttributeType | undefined;
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                type = "event_custom_attribute";
                break;
            case Item.Ids.Location:
                type = "location_custom_attribute";
                break;
            case Item.Ids.Resource:
                type = "resource_custom_attribute";
                break;
        }
        if (!type) return false;
        return MasterDefinitionUtil.isUrl(type, this.attributeType() as string, this.attributeId());
    }
}
